import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * `FE-04.10` primitive — resolved in `docs/design/stage2/B3-BRIEF.md` §4.
 *
 * `Banner` (card-back) and `Notice` (entry-surfaces) are one primitive under
 * two names; card-back flagged the collision itself and asked for the merge.
 * Named `Notice`, keeping card-back's four tones. `locked` is not folded into
 * `info` because `FE-16.3`'s item text is literally "locked with the reason,
 * not hidden" — it has to read as a state of the composer, not a notice
 * about the card.
 *
 * NEVER fold this into `EmptyState`: an `EmptyState` replaces a surface's
 * content; a `Notice` sits *above* content that is still there and still
 * usable. entry-surfaces puts the cost sharply — collapsing them "would make
 * a wrong password erase the form."
 *
 * Always on opaque `--color-surface-raised`, never `--color-panel`, because
 * its body is `--color-ink-muted` and the panel's measured floor (92%) is
 * below what muted text needs (98%).
 *
 * ⚠ **It carries that sheet itself — do NOT wrap it in `CardSurface`.** The
 * requirement is the opaque surface, not the component, and one sheet
 * discharges it; wrapping produces a nested radius and a doubled shadow that
 * reads as a styling bug. Same for `EmptyState`.
 *
 * Announces `role="status"` for `info` / `warn` / `locked` (polite, does not
 * interrupt) and `role="alert"` for `bad` (assertive).
 *
 * No dedicated `--color-info` or `--color-locked` token exists in
 * `styles.css` — only `--color-ok` / `--color-warn` / `--color-bad`. `info`
 * borrows `--color-accent` (this surface is opaque, so the "never text on a
 * translucent surface" rule for `--color-accent` does not apply here) and
 * `locked` borrows `--color-border-strong`, the token this file's own
 * conventions reserve for a control's boundary — a locked composer is
 * exactly that.
 */
const noticeVariants = cva(
  'flex items-start gap-3 rounded-card border-l-4 bg-surface-raised p-4 text-sm text-ink',
  {
    variants: {
      tone: {
        info: 'border-l-accent',
        warn: 'border-l-warn',
        bad: 'border-l-bad',
        locked: 'border-l-border-strong',
      },
    },
  },
);

const noticeIconVariants = cva('mt-0.5 shrink-0', {
  variants: {
    tone: {
      info: 'text-accent',
      warn: 'text-warn',
      bad: 'text-bad',
      locked: 'text-ink-muted',
    },
  },
});

export interface NoticeProps
  extends
    Omit<ComponentPropsWithoutRef<'div'>, 'title'>,
    VariantProps<typeof noticeVariants> {
  tone: 'info' | 'warn' | 'bad' | 'locked';
  /** Decorative — the tone itself, plus `title`/`body` text, already carry
   * the meaning, so the icon wrapper is `aria-hidden`. */
  icon?: ReactNode;
  title?: string;
  body: ReactNode;
  action?: ReactNode;
}

export function Notice({
  tone,
  icon,
  title,
  body,
  action,
  className,
  ...rest
}: NoticeProps) {
  const role = tone === 'bad' ? 'alert' : 'status';

  return (
    <div role={role} className={cn(noticeVariants({ tone }), className)} {...rest}>
      {icon ? (
        <span aria-hidden="true" className={noticeIconVariants({ tone })}>
          {icon}
        </span>
      ) : null}
      <div className="flex flex-1 flex-col gap-1">
        {title ? <p className="font-medium text-ink">{title}</p> : null}
        <div className="text-ink-muted">{body}</div>
        {action ? <div className="pt-1">{action}</div> : null}
      </div>
    </div>
  );
}
